<script>
import { mapActions } from "vuex";
import Modal from "@/components/_Shared/Modal";
import InputGroup from "@/components/_Shared/InputGroup";
import moment from "@/utils/moment";
import errorMixin from "@/mixins/errorMixin";
import { Money } from "v-money";
import DateTimePicker from "@/components/_Shared/Forms/DateTimePicker";

export default {
  mixins: [errorMixin],
  components: {
    Modal,
    InputGroup,
    Money,
    DateTimePicker
  },
  data() {
    return {
      preloader: false,
      coupon: {
        name: "",
        type: "UNRESTRICTED",
        value_type: "VALUE",
        percentage: 0,
        value: 0,
        min_applicable_value: 0,
        max_applicable_value: 0,
        max_discount_value: 0,
        expires_at: moment().toISOString(),
        max_usage: {
          total: -1,
          per_customer: -1,
          per_professional: -1,
          per_district: -1
        },
        districts: []
      },
      money: {
        decimal: ",",
        thousands: ".",
        prefix: "",
        precision: 2,
        masked: false /* doesn't work with directive */
      },
      cuoponValueType: [
        {
          value: "VALUE",
          text: "valor"
        },
        {
          value: "PERCENTAGE",
          text: "porcentagem"
        }
      ],
      couponType: [
        {
          value: "UNRESTRICTED",
          text: "sem restrições"
        },
        {
          value: "DISTRICT",
          text: "por distrito"
        }
      ]
    };
  },
  methods: {
    ...mapActions("coupons", ["create"]),

    async submit() {
      this.preloader = true;

      try {
        let valid = await this.$validator.validateAll();

        if (valid) {
          let value =
            this.coupon.value_type === "VALUE"
              ? this.coupon.value
              : this.coupon.percentage.split(/,|\./)[0];

          const data = {
            name: this.coupon.name,
            value_type: this.coupon.value_type,
            coupon_type: this.coupon.type,
            value,
            max_usage: this.coupon.max_usage,
            min_applicable_value: this.coupon.min_applicable_value,
            max_applicable_value: this.coupon.max_applicable_value,
            max_discount_value: this.coupon.max_discount_value,
            expires_at: this.coupon.expires_at
          };

          let res = await this.create(data);

          if (res) {
            this.$emit("close");
            this.$emit("refresh");
          }
        }
      } catch (error) {
        this.$emit("close");
        this.$emit("errorMessage", error[0]);
        this.$emit("snackbarError", true);
      }

      this.preloader = false;
    },

    onFormattedDate(dateTime) {
      this.coupon.expires_at = dateTime;
    }
  }
};
</script>

<template>
  <modal
    title="NOVO CUPOM"
    subtitle="Informe os dados do novo cupom"
    size="md"
    @close="$emit('close')"
  >
    <template slot="body">
      <!-- TITLE AND MAX USAGE -->
      <v-layout align-center justify-space-between row fill-height wrap>
        <v-flex
          xs12
          :class="{
            'pr-0': $vuetify.breakpoint.smAndDown,
            'pr-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="name"
            label="nome"
            v-model="coupon.name"
            :error-messages="errors.first('name')"
            :error="errors.has('name')"
            v-validate="'required|max:255'"
          />
        </v-flex>
      </v-layout>

      <!-- DATE AND TIME -->
      <date-time-picker
        dateLabel="dia da expiração"
        timeLabel="horário de expiração"
        v-model="coupon.expires_at"
      />

      <!-- TYPE AND VALUE TYPE -->
      <v-layout align-center justify-space-between row fill-height wrap>
        <v-flex
          md6
          xs12
          :class="{
            'pr-0': $vuetify.breakpoint.smAndDown,
            'pr-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-select
            name="coupon-value-type"
            label="tipo"
            v-model="coupon.value_type"
            v-validate="'required'"
            :items="cuoponValueType"
            :error-messages="errors.first('coupon-value-type')"
            :error="errors.has('coupon-value-type')"
            required
          />
        </v-flex>

        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-select
            name="coupon-type"
            label="tipo"
            v-model="coupon.type"
            v-validate="'required'"
            :items="couponType"
            :error-messages="errors.first('coupon-type')"
            :error="errors.has('coupon-type')"
            required
          />
        </v-flex>
      </v-layout>

      <v-layout row mb-10 class="title-form">VALORES</v-layout>

      <!-- VALUE AND MAX DISCOUNT VALUE -->
      <v-layout align-center justify-space-between row fill-height wrap>
        <v-flex
          md6
          xs12
          :class="{
            'pr-0': $vuetify.breakpoint.smAndDown,
            'pr-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            v-if="coupon.value_type === 'PERCENTAGE'"
            :key="coupon.value_type"
            name="percentage"
            label="porcentagem"
            suffix="%"
            v-model="coupon.percentage"
            :error="errors.has('percentage')"
            :error-messages="errors.first('percentage')"
            v-validate="'required|min_value:1|max_value:100'"
          ></v-text-field>
          <v-text-field
            v-if="coupon.value_type === 'VALUE'"
            :key="coupon.value_type"
            name="value"
            v-model="coupon.value"
            label="valor do desconto"
            v-money="money"
            prefix="R$"
            required
          ></v-text-field>
        </v-flex>

        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="max_discount_value"
            v-model="coupon.max_discount_value"
            label="valor max. de desconto"
            v-money="money"
            prefix="R$"
            required
          />
        </v-flex>
      </v-layout>

      <!-- MIN AND MAX APPLICABLE VALUE -->
      <v-layout align-center justify-space-between row fill-height wrap>
        <v-flex
          md6
          xs12
          :class="{
            'pr-0': $vuetify.breakpoint.smAndDown,
            'pr-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="min_applicable_value"
            v-model="coupon.min_applicable_value"
            label="min. aplicável"
            v-money="money"
            prefix="R$"
            required
          />
        </v-flex>

        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="max_applicable_value"
            v-model="coupon.max_applicable_value"
            label="max. aplicável"
            v-money="money"
            prefix="R$"
            required
          />
        </v-flex>
      </v-layout>
      <v-layout row class="title-form">MÁXIMO DE USO (ILIMITADO = -1)</v-layout>

      <v-layout align-center justify-space-between row fill_height wrap>
        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="maxUsageTotal"
            label="total"
            v-model="coupon.max_usage.total"
            :error="errors.has('maxUsageTotal')"
            :error-messages="errors.first('maxUsageTotal')"
            v-validate="'required|min_value:-1'"
          />
        </v-flex>

        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="maxUsagePerCustomer"
            label="por cliente"
            v-model="coupon.max_usage.per_customer"
            :error="errors.has('maxUsagePerCustomer')"
            :error-messages="errors.first('maxUsagePerCustomer')"
            v-validate="'required|min_value:-1'"
          />
        </v-flex>

        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="maxUsagePerProfessional"
            label="por profissional"
            v-model="coupon.max_usage.per_professional"
            :error="errors.has('maxUsagePerProfessional')"
            :error-messages="errors.first('maxUsagePerProfessional')"
            v-validate="'required|min_value:-1'"
          />
        </v-flex>

        <v-flex
          md6
          xs12
          :class="{
            'pl-0': $vuetify.breakpoint.smAndDown,
            'pl-1': $vuetify.breakpoint.mdAndUp
          }"
        >
          <v-text-field
            name="maxUsagePerDistrict"
            label="por bairro"
            v-model="coupon.max_usage.per_district"
            :error="errors.has('maxUsagePerDistrict')"
            :error-messages="errors.first('maxUsagePerDistrict')"
            v-validate="'required|min_value:-1'"
          />
        </v-flex>
      </v-layout>
    </template>

    <template slot="footer">
      <button class="btn bg-purple-outline" @click="$emit('close')">
        fechar
      </button>
      <button
        class="btn bg-purple"
        :class="{ 'btn--loading': preloader }"
        @click="submit"
      >
        <span>cadastrar</span>
      </button>
    </template>
  </modal>
</template>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/Coupons/NewCoupon.vue